import type { SorterResult } from 'antd/es/table/interface';
import dayjs from 'dayjs';

export type SortValueType = 'string' | 'number' | 'boolean' | 'date';

export interface SortConfig<T> {
  key: keyof T | string;
  type?: SortValueType;
  /** Custom value getter for computed columns */
  getValue?: (record: T) => string | number | boolean | Date | null | undefined;
}

/**
 * Creates a comparator function for sorting records by a specific field.
 */
export function createComparator<T>(
  key: keyof T | string,
  type: SortValueType = 'string',
  getValue?: (record: T) => string | number | boolean | Date | null | undefined
): (a: T, b: T) => number {
  return (a: T, b: T) => {
    let valueA: unknown;
    let valueB: unknown;

    if (getValue) {
      valueA = getValue(a);
      valueB = getValue(b);
    } else {
      valueA = (a as Record<string, unknown>)[key as string];
      valueB = (b as Record<string, unknown>)[key as string];
    }

    switch (type) {
      case 'number':
        return (Number(valueA) || 0) - (Number(valueB) || 0);

      case 'boolean':
        return Number(Boolean(valueA)) - Number(Boolean(valueB));

      case 'date':
        return dayjs(valueA as string | Date).valueOf() - dayjs(valueB as string | Date).valueOf();

      case 'string':
      default:
        return String(valueA ?? '').localeCompare(String(valueB ?? ''));
    }
  };
}

/**
 * Sorts an array of data based on the current sorter state and sort configuration.
 * Returns a new sorted array without mutating the original.
 */
export function sortData<T extends object>(
  data: T[],
  sorter: SorterResult<T>,
  sortConfigs: SortConfig<T>[]
): T[] {
  if (!sorter?.order || !sorter.columnKey) {
    return data;
  }

  const columnKey = sorter.columnKey as string;
  const config = sortConfigs.find((c) => c.key === columnKey);

  if (!config) {
    return data;
  }

  const comparator = createComparator<T>(config.key, config.type, config.getValue);
  const sorted = [...data].sort(comparator);

  return sorter.order === 'descend' ? sorted.reverse() : sorted;
}

/**
 * Creates sort configurations from column keys with automatic type detection.
 * Useful for simple cases where all columns are strings.
 */
export function createSortConfigs<T>(keys: (keyof T)[]): SortConfig<T>[] {
  return keys.map((key) => ({ key, type: 'string' as SortValueType }));
}

/**
 * Helper to build sort config more easily.
 */
export function sortConfig<T>(
  key: keyof T | string,
  type: SortValueType = 'string',
  getValue?: (record: T) => string | number | boolean | Date | null | undefined
): SortConfig<T> {
  return { key, type, getValue };
}
